// Lazy loading helper for heavy libraries
class LazyLibraryLoader {
  constructor() {
    this.cache = new Map();
    this.loading = new Map();
  }

  // Load specific asset file (with predefined paths to avoid webpack warning)
  async loadAssetFile(fileName) {
    const cacheKey = `asset-${fileName}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    if (this.loading.has(cacheKey)) {
      return this.loading.get(cacheKey);
    }

    let loadPromise;

    // Predefined paths to avoid webpack warning
    switch (fileName) {
      case 'buttons.html5.min':
        loadPromise = import('@/assets/backend_js/js/buttons.html5.min');
        break;
      case 'buttons.flash.min':
        loadPromise = import('@/assets/backend_js/js/buttons.flash.min');
        break;
      case 'buttons.print.min':
        loadPromise = import('@/assets/backend_js/js/buttons.print.min');
        break;
      case 'jszip.min':
        loadPromise = import('@/assets/backend_js/js/jszip.min');
        break;
      case 'pdfmake.min':
        loadPromise = import('@/assets/backend_js/js/pdfmake.min');
        break;
      case 'vfs_fonts':
        loadPromise = import('@/assets/backend_js/js/vfs_fonts');
        break;
      case 'natural.js':
        loadPromise = import('@/assets/backend_js/js/natural.js');
        break;
      default:
        throw new Error(`Unknown asset file: ${fileName}`);
    }

    const finalPromise = loadPromise.then(module => {
      const result = module.default || module;
      this.cache.set(cacheKey, result);
      this.loading.delete(cacheKey);
      return result;
    });

    this.loading.set(cacheKey, finalPromise);
    return finalPromise;
  }

  // Generic library loader with caching and loading state
  async loadLibrary(libraryName, importFunction, extractFunction = module => module.default) {
    if (this.cache.has(libraryName)) {
      return this.cache.get(libraryName);
    }

    if (this.loading.has(libraryName)) {
      return this.loading.get(libraryName);
    }

    const loadPromise = importFunction().then(module => {
      const result = extractFunction(module);
      this.cache.set(libraryName, result);
      this.loading.delete(libraryName);
      return result;
    }).catch(error => {
      this.loading.delete(libraryName);
      console.error(`Error loading library ${libraryName}:`, error);
      throw error;
    });

    this.loading.set(libraryName, loadPromise);
    return loadPromise;
  }

  // Lazy load jsPDF
  async loadJsPDF() {
    return this.loadLibrary('jspdf', () => import('jspdf'));
  }

  // Lazy load html2canvas
  async loadHtml2Canvas() {
    return this.loadLibrary('html2canvas', () => import('html2canvas'));
  }

  // Lazy load fabric.js
  async loadFabric() {
    return this.loadLibrary('fabric', () => import('fabric'), module => module.fabric);
  }

  // Lazy load swiper
  async loadSwiper() {
    return this.loadLibrary('swiper', () => import('swiper'));
  }

  // Get cache size
  getCacheSize() {
    return this.cache.size;
  }

  // Get cache info
  getCacheInfo() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      loading: Array.from(this.loading.keys())
    };
  }

  // Clear cache for specific library
  clearCache(libraryName) {
    this.cache.delete(libraryName);
  }

  // Clear all cache
  clearAllCache() {
    this.cache.clear();
    this.loading.clear();
  }
}

// Create singleton instance
const lazyLoader = new LazyLibraryLoader();

export default lazyLoader;
